<loading [visible]="loading()"></loading>

@if(integration(); as item){
<section class="flex flex-wrap justify-between items-center gap-3 mb-4">
    <div>
        <a routerLink="/integrations" class="text-xs text-indigo-600 hover:underline">← Integraciones</a>
        <h1 class="text-2xl font-bold text-gray-800 flex items-center gap-3">
            {{ item.name }}
            @if(item.enabled){
            <span class="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Activa</span>
            } @else {
            <span class="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-200 text-gray-600">Desactivada</span>
            }
        </h1>
        <a [href]="item.store_url" target="_blank" rel="noopener noreferrer"
            class="text-sm text-gray-500 hover:underline break-all">{{ item.store_url }}</a>
    </div>

    <div class="flex flex-wrap items-start gap-3">
        <div class="flex flex-col gap-1 w-44">
            <button type="button" (click)="refresh()"
                class="px-3 py-2 bg-white border border-gray-200 text-gray-700 text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-gray-50 transition-colors">
                Actualizar
            </button>
            <p class="text-xs text-gray-500">Recarga esta página para ver el resultado de las tareas lanzadas.</p>
        </div>
        <div class="flex flex-col gap-1 w-44">
            <button type="button" (click)="testConnection()" [disabled]="busy() !== null"
                class="px-3 py-2 bg-white border border-indigo-200 text-indigo-700 text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50">
                {{ busy() === 'test' ? 'Probando...' : 'Probar conexión' }}
            </button>
            <p class="text-xs text-gray-500">Comprueba que el ERP puede conectarse con la tienda. No cambia nada.</p>
        </div>
        @if(canUpdate){
        <div class="flex flex-col gap-1 w-44">
            <button type="button" (click)="analyzeAll()" [disabled]="busy() !== null"
                class="px-3 py-2 bg-white border border-indigo-200 text-indigo-700 text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50">
                {{ busy() === 'analyze' ? 'Analizando...' : 'Analizar todo' }}
            </button>
            <p class="text-xs text-gray-500">Compara productos, ventas y clientes con la tienda. No cambia nada.</p>
        </div>
        <div class="flex flex-col gap-1 w-44">
            <button type="button" (click)="syncAll()" [disabled]="busy() !== null || !item.enabled"
                class="px-3 py-2 bg-indigo-600 text-white text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                {{ busy() === 'all' ? 'Lanzando...' : 'Sincronizar todo' }}
            </button>
            <p class="text-xs text-gray-500">Sincroniza clientes, productos y ventas, en ese orden.</p>
        </div>
        <div class="flex flex-col gap-1 w-44">
            <button type="button" (click)="openEditForm()"
                class="px-3 py-2 bg-indigo-600 text-white text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-indigo-700 transition-colors">
                Configuración
            </button>
            <p class="text-xs text-gray-500">Edita los datos de conexión y los ajustes de catálogo y pedidos.</p>
        </div>
        }
    </div>
</section>

@if(item.last_error){
<div class="mb-4 p-4 rounded-lg border border-red-200 bg-red-50">
    <p class="text-xs font-semibold text-red-800 mb-1">
        Último error @if(item.last_error_at){ · {{ item.last_error_at | date:'dd/MM/yyyy HH:mm' }} }
    </p>
    <p class="text-sm text-red-700 break-words">{{ item.last_error }}</p>
</div>
}

<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
    <div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500 mb-3">Estado</h2>
        <dl class="grid grid-cols-2 gap-y-2 text-sm">
            <dt class="text-gray-500">Productos enlazados</dt>
            <dd class="text-gray-800 font-medium text-right">{{ linked.product ?? 0 }}</dd>
            <dt class="text-gray-500">Categorías enlazadas</dt>
            <dd class="text-gray-800 font-medium text-right">{{ linked.category ?? 0 }}</dd>
            <dt class="text-gray-500">Clientes enlazados</dt>
            <dd class="text-gray-800 font-medium text-right">{{ linked.client ?? 0 }}</dd>
            <dt class="text-gray-500">Pedidos importados</dt>
            <dd class="text-gray-800 font-medium text-right">{{ linked.order ?? 0 }}</dd>
            <dt class="text-gray-500">Webhooks registrados</dt>
            <dd class="text-right font-medium" [class.text-green-700]="webhookCount >= 5" [class.text-amber-700]="webhookCount < 5">
                {{ webhookCount }} / 5
            </dd>
            <dt class="text-gray-500">Pedidos revisados hasta</dt>
            <dd class="text-gray-800 text-right">{{ item.orders_synced_at ? (item.orders_synced_at | date:'dd/MM/yyyy HH:mm') : '—' }}</dd>
            <dt class="text-gray-500">Precios de la tienda</dt>
            <dd class="text-gray-800 text-right">{{ item.settings?.shop_prices_include_tax ? 'Con IVA' : 'Sin IVA' }}</dd>
        </dl>
        @if(webhookCount < 5 && item.enabled){
        <p class="text-xs text-amber-700 mt-3">
            Los webhooks se registran en segundo plano. Si siguen sin aparecer, revisa los logs: la tienda debe poder
            llegar a la URL pública del ERP.
        </p>
        }
    </div>

    <div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500 mb-3">Productos</h2>
        <p class="text-xs text-gray-500 mb-4">
            Los productos se emparejan por SKU = referencia del ERP. Cada cambio en el ERP se envía solo a la tienda,
            y los productos nuevos publicados en la tienda con SKU entran solos en el ERP. Los botones solo hacen falta
            para forzarlo.
        </p>
        @if(canUpdate){
        <div class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
                <button type="button" (click)="sync('catalog')" [disabled]="busy() !== null || !item.enabled"
                    class="px-3 py-2 bg-indigo-600 text-white text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                    Sincronizar productos
                </button>
                <p class="text-xs text-gray-500">Enlaza los productos por SKU y sube a la tienda los del ERP con sus categorías, precios y stock.</p>
            </div>
            <div class="flex flex-col gap-1">
                <button type="button" (click)="sync('stock')" [disabled]="busy() !== null || !item.enabled"
                    class="px-3 py-2 bg-white border border-gray-200 text-gray-700 text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
                    Reenviar stock y precios
                </button>
                <p class="text-xs text-gray-500">Vuelve a enviar los productos ya enlazados. Los que no han cambiado desde el último envío se omiten.</p>
            </div>
            <div class="flex flex-col gap-1">
                <button type="button" (click)="sync('import')" [disabled]="busy() !== null || !item.enabled"
                    class="px-3 py-2 bg-white border border-gray-200 text-gray-700 text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50">
                    Importar productos de la tienda
                </button>
                <p class="text-xs text-gray-500">Trae ahora los productos que solo están en la tienda, también borradores, y restaura los borrados en el ERP. Los que ya existen no se modifican.</p>
            </div>
        </div>
        }
    </div>

    <div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500 mb-3">Ventas</h2>
        <p class="text-xs text-gray-500 mb-4">
            Los pedidos nuevos llegan solos. Aquí puedes traer los pedidos modificados desde una fecha
            (los ya importados se omiten).
        </p>
        @if(canUpdate){
        <div class="flex flex-wrap items-end gap-2">
            <label class="flex flex-col text-xs text-gray-600">
                Desde
                <input type="date" [(ngModel)]="importSince" [max]="today"
                    class="mt-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-800" />
            </label>
            <button type="button" (click)="importOrders()" [disabled]="busy() !== null || !item.enabled"
                class="px-3 py-2 bg-indigo-600 text-white text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                {{ busy() === 'import' ? 'Sincronizando...' : 'Sincronizar ventas' }}
            </button>
        </div>
        <p class="text-xs text-gray-500 mt-2">Trae al ERP los pedidos de la tienda modificados desde la fecha indicada.</p>
        }
    </div>

    <div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500 mb-3">Clientes</h2>
        <p class="text-xs text-gray-500 mb-4">
            Los clientes se emparejan por email y se sincronizan solos en los dos sentidos. Los del ERP necesitan un
            email válido para tener cuenta en la tienda. El botón solo hace falta para forzarlo.
        </p>
        @if(canUpdate){
        <div class="flex flex-col gap-1">
            <button type="button" (click)="sync('customers')" [disabled]="busy() !== null || !item.enabled"
                class="px-3 py-2 bg-indigo-600 text-white text-xs font-semibold uppercase tracking-wider rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                Sincronizar clientes
            </button>
            <p class="text-xs text-gray-500">Trae al ERP los clientes de la tienda y crea en la tienda los clientes del ERP con email que aún no tengan cuenta.</p>
        </div>
        }
    </div>
</div>

<div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-4">
    <div class="flex flex-wrap justify-between items-center gap-2 mb-3">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500">Informe de catálogo</h2>
        @if(report()){
        <span class="text-xs text-gray-400">
            {{ report().applied ? 'Sincronizado' : 'Analizado' }} el {{ report().generated_at | date:'dd/MM/yyyy HH:mm' }}
        </span>
        }
    </div>

    @if(report(); as r){
    <div class="flex flex-wrap gap-2 mb-3">
        <button type="button" (click)="reportTab.set('only_in_erp')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="reportTab() === 'only_in_erp' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600'">
            Solo en el ERP ({{ r.counts?.only_in_erp ?? 0 }})
        </button>
        <button type="button" (click)="reportTab.set('only_in_shop')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="reportTab() === 'only_in_shop' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600'">
            Solo en la tienda ({{ r.counts?.only_in_shop ?? 0 }})
        </button>
        <button type="button" (click)="reportTab.set('matched')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="reportTab() === 'matched' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-white border-gray-200 text-gray-600'">
            Emparejados ({{ r.counts?.matched ?? 0 }})
        </button>
    </div>

    <p class="text-xs text-gray-500 mb-2">
        @switch(reportTab()){
        @case('only_in_erp'){ Sin producto con el mismo SKU en la tienda: al sincronizar el catálogo se crearán en ella. }
        @case('only_in_shop'){ Productos de la tienda sin referencia equivalente en el ERP: la sincronización no los toca. Usa «Importar productos de la tienda» para crearlos en el ERP, o revisa su SKU si deberían emparejarse. }
        @case('matched'){ Productos del ERP enlazados con su producto de la tienda. }
        }
    </p>

    <div class="max-h-80 overflow-auto border border-gray-100 rounded-lg">
        <table class="w-full text-sm">
            <thead class="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0">
                <tr>
                    <th class="text-left px-3 py-2">{{ reportTab() === 'only_in_shop' ? 'SKU' : 'Referencia' }}</th>
                    <th class="text-left px-3 py-2">Nombre</th>
                    @if(reportTab() !== 'only_in_erp'){ <th class="text-left px-3 py-2">ID tienda</th> }
                </tr>
            </thead>
            <tbody>
                @for(row of r[reportTab()] ?? []; track $index){
                <tr class="border-t border-gray-100">
                    <td class="px-3 py-1.5 font-mono text-xs">{{ row.code ?? row.sku ?? '—' }}</td>
                    <td class="px-3 py-1.5">{{ row.name }}</td>
                    @if(reportTab() !== 'only_in_erp'){ <td class="px-3 py-1.5 text-gray-500">{{ row.external_id }}</td> }
                </tr>
                } @empty {
                <tr><td colspan="3" class="px-3 py-4 text-center text-gray-400">Nada que mostrar</td></tr>
                }
            </tbody>
        </table>
    </div>
    } @else {
    <p class="text-sm text-gray-500">
        Todavía no se ha analizado el catálogo. Pulsa «Analizar todo» y, cuando termine, «Actualizar».
    </p>
    }
</div>

<div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-4">
    <div class="flex flex-wrap justify-between items-center gap-2 mb-3">
        <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500">Informe de pedidos</h2>
        <label class="flex items-center gap-2 text-xs text-gray-600">
            Modificados desde
            <input type="date" [(ngModel)]="importSince" (ngModelChange)="getOrdersReport()" [max]="today"
                class="px-2 py-1 border border-gray-200 rounded-lg text-sm text-gray-800" />
        </label>
    </div>

    @if(ordersReport(); as r){
    <div class="flex flex-wrap gap-2 mb-3">
        <button type="button" (click)="ordersTab.set('imported')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="ordersTab() === 'imported' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-white border-gray-200 text-gray-600'">
            Importados ({{ r.counts?.imported ?? 0 }})
        </button>
        <button type="button" (click)="ordersTab.set('pending')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="ordersTab() === 'pending' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600'">
            Pendientes de importar ({{ r.counts?.pending ?? 0 }})
        </button>
        <button type="button" (click)="ordersTab.set('ignored')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="ordersTab() === 'ignored' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600'">
            No se importan ({{ r.counts?.ignored ?? 0 }})
        </button>
    </div>

    <p class="text-xs text-gray-500 mb-2">
        @switch(ordersTab()){
        @case('imported'){ Pedidos de la tienda que ya tienen su documento en el ERP. }
        @case('pending'){ Pedidos que deberían estar en el ERP y aún no están. Usa «Sincronizar ventas» para traerlos; si siguen aquí, busca el motivo en el registro. }
        @case('ignored'){ Pedidos en un estado que la integración no importa. Los estados se eligen en «Configuración». }
        }
    </p>

    <div class="max-h-80 overflow-auto border border-gray-100 rounded-lg">
        <table class="w-full text-sm">
            <thead class="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0">
                <tr>
                    <th class="text-left px-3 py-2">Pedido</th>
                    <th class="text-left px-3 py-2">Fecha</th>
                    <th class="text-left px-3 py-2">Comprador</th>
                    <th class="text-left px-3 py-2">Estado</th>
                    <th class="text-right px-3 py-2">Total</th>
                    @if(ordersTab() === 'imported'){ <th class="text-left px-3 py-2">Documento ERP</th> }
                </tr>
            </thead>
            <tbody>
                @for(row of r[ordersTab()] ?? []; track row.external_id){
                <tr class="border-t border-gray-100">
                    <td class="px-3 py-1.5 font-mono text-xs">#{{ row.number }}</td>
                    <td class="px-3 py-1.5 text-gray-500">{{ row.date ? (row.date | date:'dd/MM/yyyy HH:mm') : '—' }}</td>
                    <td class="px-3 py-1.5">{{ row.customer || '—' }}</td>
                    <td class="px-3 py-1.5">{{ orderStatusLabel(row.status) }}</td>
                    <td class="px-3 py-1.5 text-right" [centToEurDisplay]="row.total_cents"></td>
                    @if(ordersTab() === 'imported'){
                    <td class="px-3 py-1.5 text-gray-500">{{ row.local_type === 'ticket' ? 'Ticket' : 'Albarán' }} {{ row.local_number ?? '—' }}</td>
                    }
                </tr>
                } @empty {
                <tr><td colspan="6" class="px-3 py-4 text-center text-gray-400">Nada que mostrar</td></tr>
                }
            </tbody>
        </table>
    </div>
    } @else if(ordersReportError()){
    <p class="text-sm text-red-700">{{ ordersReportError() }}</p>
    } @else {
    <p class="text-sm text-gray-500">Consultando los pedidos de la tienda...</p>
    }
</div>

<div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-4">
    <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500 mb-3">Informe de clientes</h2>

    @if(customersReport(); as r){
    <div class="flex flex-wrap gap-2 mb-3">
        <button type="button" (click)="customersTab.set('matched')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="customersTab() === 'matched' ? 'bg-green-50 border-green-300 text-green-800' : 'bg-white border-gray-200 text-gray-600'">
            Enlazados ({{ r.counts?.matched ?? 0 }})
        </button>
        <button type="button" (click)="customersTab.set('only_in_shop')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="customersTab() === 'only_in_shop' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600'">
            Solo en la tienda ({{ r.counts?.only_in_shop ?? 0 }})
        </button>
        <button type="button" (click)="customersTab.set('only_in_erp')"
            class="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
            [class]="customersTab() === 'only_in_erp' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-gray-200 text-gray-600'">
            Solo en el ERP ({{ r.counts?.only_in_erp ?? 0 }})
        </button>
    </div>

    <p class="text-xs text-gray-500 mb-2">
        @switch(customersTab()){
        @case('matched'){ Clientes de la tienda con su cliente del ERP. Sus próximos pedidos entrarán a su nombre. }
        @case('only_in_shop'){ Clientes sin enlazar todavía: «Sincronizar clientes» los enlaza con el cliente del ERP con su mismo email, o los crea. }
        @case('only_in_erp'){ Clientes del ERP sin cuenta enlazada en la tienda. Los que tienen un email válido se crean solos; el resto seguirá aquí hasta que se lo añadas. }
        }
    </p>

    <div class="max-h-80 overflow-auto border border-gray-100 rounded-lg">
        <table class="w-full text-sm">
            <thead class="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0">
                <tr>
                    <th class="text-left px-3 py-2">Nombre</th>
                    <th class="text-left px-3 py-2">Email</th>
                    @if(customersTab() === 'matched'){ <th class="text-left px-3 py-2">Cliente ERP</th> }
                    @if(customersTab() !== 'only_in_erp'){ <th class="text-left px-3 py-2">ID tienda</th> }
                </tr>
            </thead>
            <tbody>
                @for(row of r[customersTab()] ?? []; track $index){
                <tr class="border-t border-gray-100">
                    <td class="px-3 py-1.5">{{ row.name }}</td>
                    <td class="px-3 py-1.5 text-gray-500 break-all">{{ row.email || '—' }}</td>
                    @if(customersTab() === 'matched'){ <td class="px-3 py-1.5">{{ row.client_name }}</td> }
                    @if(customersTab() !== 'only_in_erp'){ <td class="px-3 py-1.5 text-gray-500">{{ row.external_id }}</td> }
                </tr>
                } @empty {
                <tr><td colspan="4" class="px-3 py-4 text-center text-gray-400">Nada que mostrar</td></tr>
                }
            </tbody>
        </table>
    </div>
    } @else if(customersReportError()){
    <p class="text-sm text-red-700">{{ customersReportError() }}</p>
    } @else {
    <p class="text-sm text-gray-500">Consultando los clientes de la tienda...</p>
    }
</div>

<div class="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
    <h2 class="text-sm font-semibold uppercase tracking-wider text-indigo-500 mb-3">Registro</h2>
    <sau-table #sauTable [url]="logsUrl" [filterConfig]="logFilterConfig" contentList="list" contentTotal="total"
        pageParamName="page" limitParamName="limit" [sizeInitialPage]="1" [sizeBetweenPages]="1" [limit]="20"
        [headers]="logHeaders" [showOptions]="false"></sau-table>
</div>
}
